import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Button, Typography } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useUploadDiagnosisCodesMutation, useGetDiagnosisLoadStatusQuery } from '../../../../services/ConfigurationService';
import { useSignalR } from '../../../../hooks/useSignalR';
import { UploadProgress } from '../../../../services/signalRService';

interface DiagnosisUploadFileProps {
  onUploadStart?: () => void;
  onProgress?: (progress: UploadProgress) => void;
  onCompleted?: (progress: UploadProgress) => void;
  // A pre-202 rejection (a 400 from either guard clause shape) - nothing was written.
  onRejected?: (message: string) => void;
  // A SignalR error after the 202 - the background write had already started, so the catalog may
  // be partly updated.
  onOperationFailed?: (message: string) => void;
}

const parseUploadError = (err: any): string => {
  const data = err?.data;
  if (typeof data === 'string' && data.trim() !== '') return data;
  if (data?.errors?.length) return data.errors[0];
  return err?.message || 'Upload failed';
};

// Owns the one upload this component can have running. The backend starts the load-and-prune write
// on a fire-and-forget task before the 202 returns, so a small file's completion event can arrive
// before the browser's own POST does, and the hub is per-user rather than per-operation. So an
// incoming event is ours through three states: nothing submitted (drop it), submitted but the id
// isn't known yet (accept it - only one of our operations is ever in flight, see below), and the id
// is known (require the exact match). "In flight" spans the whole operation, from submission to the
// matching completion/error event, not just the HTTP round trip - that's what keeps the upload
// button disabled for as long as the write is actually running.
const useDiagnosisUpload = (
  onUploadStart: (() => void) | undefined,
  onProgress: ((progress: UploadProgress) => void) | undefined,
  onCompleted: ((progress: UploadProgress) => void) | undefined,
  onRejected: ((message: string) => void) | undefined,
  onOperationFailed: ((message: string) => void) | undefined,
) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasSubmittedRef = useRef(false);
  const operationIdRef = useRef<string | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [uploadDiagnosisCodes] = useUploadDiagnosisCodesMutation();
  const { data: runningLoad } = useGetDiagnosisLoadStatusQuery();

  // A page opened mid-load adopts the operation the service reports: the hub is per-user, so that
  // operation's progress and completion events then arrive here like any other, and the button
  // stays disabled for as long as the write runs. Without this a reload showed a clean page with a
  // live upload button while 74,000 rows were still writing (spec 131 verification, round 2).
  useEffect(() => {
    if (!runningLoad || hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    operationIdRef.current = runningLoad.operationId;
    setInFlight(true);
    onUploadStart?.();
    onProgress?.(runningLoad);
  }, [runningLoad, onUploadStart, onProgress]);

  const isOwnEvent = useCallback(
    (progress: UploadProgress) =>
      hasSubmittedRef.current
      && (operationIdRef.current === null || progress.operationId === operationIdRef.current),
    [],
  );

  const finish = useCallback(() => {
    hasSubmittedRef.current = false;
    operationIdRef.current = null;
    setInFlight(false);
  }, []);

  const handleProgress = useCallback(
    (progress: UploadProgress) => {
      if (isOwnEvent(progress)) onProgress?.(progress);
    },
    [isOwnEvent, onProgress],
  );

  const handleCompleted = useCallback(
    (progress: UploadProgress) => {
      if (!isOwnEvent(progress)) return;
      finish();
      onCompleted?.(progress);
    },
    [isOwnEvent, finish, onCompleted],
  );

  const handleSignalRError = useCallback(
    (progress: UploadProgress) => {
      if (!isOwnEvent(progress)) return;
      finish();
      onOperationFailed?.(progress.message);
    },
    [isOwnEvent, finish, onOperationFailed],
  );

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        hasSubmittedRef.current = true;
        // A small file's completion can arrive before its own 202 resolves; finish() has then
        // already run, and the late unwrap leaves the previous operation's id behind. Clear it
        // here so this upload's events are not filtered against a stale id.
        operationIdRef.current = null;
        setInFlight(true);
        onUploadStart?.();
        try {
          const result = await uploadDiagnosisCodes({ file }).unwrap();
          operationIdRef.current = result.operationId;
        } catch (err) {
          finish();
          onRejected?.(parseUploadError(err));
        }
      }

      // Clear the input so the same file can be uploaded again
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [onUploadStart, onRejected, uploadDiagnosisCodes, finish],
  );

  return {
    fileInputRef,
    inFlight,
    handleUploadClick,
    handleFileChange,
    handleProgress,
    handleCompleted,
    handleSignalRError,
  };
};

const DiagnosisUploadFile: React.FC<DiagnosisUploadFileProps> = ({
  onUploadStart,
  onProgress,
  onCompleted,
  onRejected,
  onOperationFailed,
}) => {
  const {
    fileInputRef,
    inFlight,
    handleUploadClick,
    handleFileChange,
    handleProgress,
    handleCompleted,
    handleSignalRError,
  } = useDiagnosisUpload(onUploadStart, onProgress, onCompleted, onRejected, onOperationFailed);

  const { isConnected } = useSignalR({
    serviceName: 'Configuration',
    uploadType: 'Diagnosis', // Match the UploadType constant in backend
    onProgress: handleProgress,
    onCompleted: handleCompleted,
    onError: handleSignalRError,
    autoConnect: true,
  });

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".txt"
        style={{ display: 'none' }}
      />
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={handleUploadClick}
        size="small"
        disabled={!isConnected || inFlight}
      >
        Upload File
      </Button>
      {!isConnected && (
        <Typography variant="caption" color="text.secondary">
          Waiting for a live connection before upload can start.
        </Typography>
      )}
    </>
  );
};

export default DiagnosisUploadFile;
